/**
 * @fileOverview Defines a pagination component for paging through record sets,
 * shows a summary of which records you are viewing together with the total
 * amount of records. Provides a left and right paging arrow and an input to
 * quickly navigate to a specific page.
 */

import React, { Fragment } from 'react'
import injectStyles from 'react-jss'
import classNames from 'classnames'
import { numberFormatter } from '../../Utils/Formatters'
import { Select, MenuItem, IconButton } from '@material-ui/core'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'

const styles = {
  container: {
    marginTop: 15,
    marginBottom: 15,
    textAlign: 'right',
    '& .MuiSelect-select': {
      fontSize: '0.875rem',
    },
    '& .MuiInputBase-root': {
      margin: '0 15px 0 5px',
    },
  },
  arrow: {
    width: 24,
    marginLeft: 24,
    cursor: 'pointer',
  },
  input: {
    border: '1px solid #dee2e5',
    backgroundColor: 'white',
    padding: '5px 6px',
  },
}

/**
 * @typedef PaginationProps
 * @property {number} total The total amount of records
 * @property {number} offset The offset (or amount of records skipped) before
 * indicating which records are shown
 * @property {number} resultsPerPage The amount of records to show per page
 * @property {array} rowsPerPageOptions The amount of records to show per page
 * @property {Function} onOffsetChange When the offset is changed this method
 * will be triggered, it's only argument will be the new offset value.
 *
 * @param {PaginationProps} props
 */

const Pagination = props => {
  const {
    classes,
    className,
    total,
    offset,
    resultsPerPage,
    onOffsetChange,
    rowsPerPageOptions = [10, 20, 50, 100],
    setResultsPerPage,
  } = props

  if (total === undefined || total === null)
    throw new Error("Missing prop 'total'.")

  if (offset === undefined || offset === null)
    throw new Error("Missing prop 'offset'.")

  if (resultsPerPage === undefined || resultsPerPage === null)
    throw new Error("Missing prop 'resultsPerPage'.")

  const onChangeItemsPerPage = e => {
    const items = Number(e.target.innerText) || Number(e.target.value)
    if (isNaN(items)) return
    offset < items && onOffsetChange(0)
    setResultsPerPage && setResultsPerPage(items)
  }

  const first = offset + 1,
    last = Math.min(offset + resultsPerPage, total)

  const totalPages = Math.ceil(total / resultsPerPage),
    page = Math.round(offset / resultsPerPage) + 1

  // When there's no records we show a different summary
  if (total === 0) {
    return (
      <div className={classNames(classes.container, className)}>
        showing 0 records
      </div>
    )
  }

  return (
    <div className={classNames(classes.container, className, 'Pagination')}>
      {rowsPerPageOptions.length && setResultsPerPage && (
        <Fragment>
          Rows per page:{' '}
          <Select
            value={resultsPerPage}
            onChange={onChangeItemsPerPage}
            disableUnderline={true}
          >
            {rowsPerPageOptions.map(value => (
              <MenuItem value={value} key={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </Fragment>
      )}{' '}
      {numberFormatter(first)}-{numberFormatter(last)} of{' '}
      {numberFormatter(total)}
      <IconButton
        onClick={() =>
          onOffsetChange(offset < resultsPerPage ? 0 : offset - resultsPerPage)
        }
        title="Previous page"
        disabled={page === 1}
      >
        <ChevronLeftIcon />
      </IconButton>
      <IconButton
        onClick={() => onOffsetChange(offset + resultsPerPage)}
        title="Next page"
        disabled={page === totalPages}
      >
        <ChevronRightIcon />
      </IconButton>
    </div>
  )
}

const StyledPagination = injectStyles(styles)(Pagination)

export default StyledPagination
